import { makeStyles } from '@material-ui/core';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { Fragment, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from '~components/loading';
import SliderTab from '~components/slider-tab';
import Typography from '~components/typography';
import { useReplaceSuiteStudentToCustomer } from '~containers/suite-config/suite-context';
import { PicFilterNoResults } from '~svg-components';
import BookingCard from './booking-card';
import ContactCard from './contact-card';
import NominationPlanCard from './nomination-plan-card';
import PaymentItem from './payment-item';
import StudentCard from './student-card';
import TransactionRecord from './transaction-record';

const useStyles = makeStyles(
  (theme) => ({
    root: {
      position: 'absolute',
      width: '616px',
      backgroundColor: theme.colors.white,
      zIndex: '999',
      boxShadow: theme.shadow.componentShallow,
      borderRadius: theme.borderRadius.big,
      border: `1px solid ${theme.colors.greyE8}`,
      overflow: 'hidden',
    },

    contentRoot: {
      padding: '16px 20px 12px',
    },

    flex: {
      ...theme.flex.mainCenter,
      alignItems: 'stretch',
    },

    studentFullFlex: { flex: 1 },

    partOfBooking: {
      flex: 1,
    },

    booking: {
      flex: 1,
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: 6,
        height: 6,
        display: 'block',
        backgroundColor: theme.colors.white,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.colors.greyE8,
        borderRadius: 20,
      },
    },

    partOfStudent: {
      ...theme.flex.mainStart,
      flex: 1,
    },

    partOfTransaction: {
      flex: 1,
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: 6,
        height: 6,
        display: 'block',
        backgroundColor: theme.colors.white,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.colors.greyE8,
        borderRadius: 20,
      },
    },

    student: {
      width: 360,
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: 6,
        height: 6,
        display: 'block',
        backgroundColor: theme.colors.white,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.colors.greyE8,
        borderRadius: 20,
      },
    },

    emergencyContactsGuarantor: {
      width: '216px',
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: 6,
        height: 6,
        display: 'block',
        backgroundColor: theme.colors.white,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.colors.greyE8,
        borderRadius: 20,
      },
    },

    emergencyContacts: {
      flex: 1,
      marginLeft: '32px',
    },

    guarantors: {
      flex: 1,
      marginLeft: '32px',
    },

    divider: {
      backgroundColor: theme.colors.greyE8,
      height: '1px',
      margin: '20px 0px 16px 32px',
    },

    notFoundResult: {
      ...theme.flex.center,
      height: '320px',
      width: '100%',
    },

    notFoundResultIcon: {
      width: '168px',
      height: '118px',
      marginBottom: '10px',
    },

    marginTop20: {
      marginTop: '20px',
    },

    partOfGroupPlan: {
      flex: 1,
    },

    loadingPage: {
      ...theme.flex.center,
      position: 'absolute',
      width: 616,
      height: '100%',
      zIndex: '999',
      backgroundColor: theme.colors.white,
      boxShadow: theme.shadow.componentShallow,
      borderRadius: theme.borderRadius.big,
      border: `1px solid ${theme.colors.greyE8}`,
    },
  }),
  { name: 'SearchPanel' }
);

const SearchPanel = React.forwardRef(
  (
    {
      globalSearchBookingAndStudentResult,
      globalSearchBillingResult,
      globalSearchNominationPlan,
      loading,
      handleClickSearchStudentAndBooking,
      handleClickSearchBilling,
      handleClickSearchNominationPlan,
    },
    ref
  ) => {
    const { t } = useTranslation('global-search');
    const classes = useStyles();
    const [currentTab, setCurrentTab] = useState('booking');
    const replaceSuiteStudentToCustomer = useReplaceSuiteStudentToCustomer();

    const { bookings = [], students = [], emergencyContacts = [], guarantors = [] } =
      globalSearchBookingAndStudentResult || {};

    const { transactionRecords = [], paymentItems = [] } = globalSearchBillingResult || {};

    const { nominationPlans = [] } = globalSearchNominationPlan || {};

    const handleClickChangeTab = (item) => {
      setCurrentTab(item);
      if (item === 'student' || item === 'booking') {
        handleClickSearchStudentAndBooking();
      }
      if (item === 'transaction') {
        handleClickSearchBilling();
      }
      if (item === 'group_plan') {
        handleClickSearchNominationPlan();
      }
    };

    useImperativeHandle(ref, () => ({
      currentTab,
    }));

    const content = {
      student: replaceSuiteStudentToCustomer(t('no_student_found'), t('no_customer_found')),
      booking: t('no_booking_found'),
      transaction: t('no_transaction_found'),
      group_plan: t('no_group_plan_found'),
    };

    const notFoundSearchResult = () => {
      if (currentTab === 'student' && (students || []).length > 0) return false;
      if (currentTab === 'booking' && (bookings || []).length > 0) return false;
      if (currentTab === 'group_plan' && (nominationPlans || []).length > 0) return false;
      if (
        currentTab === 'transaction' &&
        ((transactionRecords || []).length > 0 || (paymentItems || []).length > 0)
      ) {
        return false;
      }
      return true;
    };

    const renderLoading = () =>
      loading && (
        <div className={classes.loadingPage}>
          <Loading />
        </div>
      );

    const renderEmptyContainer = () => (
      <div className={classes.notFoundResult}>
        <div style={{ textAlign: 'center' }}>
          <PicFilterNoResults className={classes.notFoundResultIcon} />
          <Typography variant="body1" themeColor="grey66">
            {content[currentTab]}
          </Typography>
        </div>
      </div>
    );

    const renderPartOfStudent = () => (
      <div className={classes.partOfStudent}>
        {students?.length > 0 && (
          <div
            className={classnames(classes.student, {
              [classes.studentFullFlex]: emergencyContacts.length === 0 && guarantors.length === 0,
            })}
            style={{ maxHeight: window.innerHeight - 86 - 40 - 16 }}
          >
            {students.map((student, index) => (
              <Fragment key={`student-${index}`}>
                <StudentCard student={student} key={student.studentId} />
              </Fragment>
            ))}
          </div>
        )}

        {(emergencyContacts.length > 0 || guarantors.length > 0) && (
          <div
            className={classes.emergencyContactsGuarantor}
            style={{ maxHeight: window.innerHeight - 86 - 40 - 20 }}
          >
            {emergencyContacts.length > 0 && (
              <div className={classes.emergencyContacts}>
                <Typography variant="h6" themeColor="grey99" style={{ marginBottom: 12 }}>
                  {t('emergency_contact')}
                </Typography>
                {emergencyContacts.map((emergencyContact, index) => (
                  <Fragment key={`emergencyContact-${index}`}>
                    <ContactCard contact={emergencyContact} />
                  </Fragment>
                ))}
              </div>
            )}
            {guarantors.length > 0 && emergencyContacts.length > 0 && (
              <div className={classes.divider} />
            )}
            {guarantors.length > 0 && (
              <div className={classes.guarantors}>
                <Typography variant="h6" themeColor="grey99" style={{ marginBottom: 12 }}>
                  {t('guarantor')}
                </Typography>
                {guarantors.map((guarantor, index) => (
                  <Fragment key={`guarantor-${index}`}>
                    <ContactCard contact={guarantor} isGuarantor />
                  </Fragment>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );

    const renderPartOfBooking = () => (
      <div className={classes.partOfBooking}>
        <div className={classes.booking} style={{ maxHeight: window.innerHeight - 86 - 40 - 16 }}>
          {bookings.map((booking, index) => (
            <Fragment key={`booking-${index}`}>
              <BookingCard booking={booking} />
            </Fragment>
          ))}
        </div>
      </div>
    );

    const renderPartOfTransaction = () => (
      <div
        className={classes.partOfTransaction}
        style={{ maxHeight: window.innerHeight - 86 - 40 - 16 }}
      >
        {paymentItems.length > 0 && (
          <div className={classes.paymentItem}>
            <Typography variant="h6" themeColor="grey99" style={{ marginBottom: 12 }}>
              {t('payment_item')}
            </Typography>
            {paymentItems.map((payment, index) => (
              <Fragment key={`payment-${index}`}>
                <PaymentItem payment={payment} />
              </Fragment>
            ))}
          </div>
        )}
        {transactionRecords.length > 0 && (
          <div
            className={classnames(classes.transaction, {
              [classes.marginTop20]: paymentItems.length > 0,
            })}
          >
            <Typography variant="h6" themeColor="grey99" style={{ marginBottom: 12 }}>
              {t('transaction_record')}
            </Typography>
            {transactionRecords.map((transactionRecord, index) => (
              <Fragment key={`transactionRecord-${index}`}>
                <TransactionRecord transactionRecord={transactionRecord} />
              </Fragment>
            ))}
          </div>
        )}
      </div>
    );

    const renderPartOfGroupPlan = () => (
      <div className={classes.partOfGroupPlan}>
        {(nominationPlans || []).map((nominationPlan, index) => (
          <Fragment key={`nomination-plan-${index}`}>
            <NominationPlanCard nominationPlan={nominationPlan} />
          </Fragment>
        ))}
      </div>
    );

    return (
      <div className={classes.root} style={{ maxHeight: window.innerHeight - 86 }}>
        {renderLoading()}
        <div className={classes.contentRoot}>
          <SliderTab
            onClickTab={handleClickChangeTab}
            tabs={['booking', 'student', 'transaction', 'group_plan'].map((item) => ({
              value: item,
              label:
                item === 'student'
                  ? replaceSuiteStudentToCustomer(t('student'), t('customer'))
                  : t(item),
            }))}
          />
          <div className={classes.flex}>
            {notFoundSearchResult() && renderEmptyContainer()}
            {currentTab === 'booking' && renderPartOfBooking()}
            {currentTab === 'student' && renderPartOfStudent()}
            {currentTab === 'transaction' && renderPartOfTransaction()}
            {currentTab === 'group_plan' && renderPartOfGroupPlan()}
          </div>
        </div>
      </div>
    );
  }
);

SearchPanel.prototype = {
  globalSearchResult: PropTypes.object,
};

SearchPanel.defaultProps = {
  globalSearchResult: {},
};

export default SearchPanel;
